import { clamp } from './utils.js';
import { getAudioCtx, scheduleClick, scheduleDownbeatTone } from './audio.js';

/**
 * @typedef {object} Tempo
 * @property {number} bpm
 * @property {number} beatsPerBar
 * @property {number} subdivision
 * @property {number} swingPct
 */

/**
 * @typedef {object} SchedulerDeps
 * @property {() => Tempo} getTempo
 * @property {() => number} getScheduleAheadTime
 * @property {(t: number) => void} setNextEventTime
 * @property {() => number} getNextEventTime
 * @property {(idx: number) => void} setStepIndex
 * @property {() => number} getStepIndex
 * @property {() => number[]} getStressLevels
 * @property {() => boolean} getDownbeatEnabled
 * @property {() => {
 * primaryPitchHz: number,
 * secondaryPitchHz: number,
 * unstressedPitchHz: number,
 * primaryDecayMs: number,
 * secondaryDecayMs: number,
 * unstressedDecayMs: number,
 * primaryStressVolPct: number,
 * secondaryStressVolPct: number,
 * unstressedVolPct: number,
 * roleFrequencyHz: (role: "bar" | "beat" | "sub", baseHz: number) => number,
 * roleDecaySeconds: (role: "bar" | "beat" | "sub", baseDecaySec: number) => number
 * }} getAudioParams
 * @property {(evt: { stepIdx: number, stepInBar: number, role: "bar" | "beat" | "sub", delayMs: number }) => void} [onVisual]
 */

/** @param {SchedulerDeps} deps */
export function createScheduler({
    getTempo,
    getScheduleAheadTime,
    setNextEventTime,
    getNextEventTime,
    setStepIndex,
    getStepIndex,
    getStressLevels,
    getDownbeatEnabled,
    getAudioParams,
    onVisual,
}) {
    /** @param {number} bpm */
    function secondsPerBeat(bpm) {
        return 60.0 / bpm;
    }

    /** @param {Pick<Tempo, "bpm" | "subdivision">} param0 */
    function baseStepSeconds({ bpm, subdivision }) {
        return secondsPerBeat(bpm) / subdivision;
    }

    /** @param {number} stepIdx @param {Tempo} param1 */
    function stepDeltaSecondsForIndex(stepIdx, { bpm, subdivision, swingPct }) {
        const base = baseStepSeconds({ bpm, subdivision });
        if (swingPct <= 0) return base;
        if (!(subdivision === 2 || subdivision === 4)) return base;

        const pairTotal = 2 * base;
        const t = clamp(swingPct / 100, 0, 0.8);
        const longFrac = 0.5 + 0.25 * t;
        const shortFrac = 1.0 - longFrac;
        const isFirstInPair = stepIdx % 2 === 0;
        return isFirstInPair ? pairTotal * longFrac : pairTotal * shortFrac;
    }

    /** @param {number} stepIdx @param {Pick<Tempo, "beatsPerBar" | "subdivision">} param1 */
    function beatNumberInBar(stepIdx, { beatsPerBar, subdivision }) {
        const stepsPerBeat = subdivision;
        const stepsPerBar = beatsPerBar * stepsPerBeat;
        const stepInBar = ((stepIdx % stepsPerBar) + stepsPerBar) % stepsPerBar;
        return Math.floor(stepInBar / stepsPerBeat) + 1;
    }

    /** @param {number} stepIdx @param {Pick<Tempo, "beatsPerBar" | "subdivision">} param1 */
    function stepRole(stepIdx, { beatsPerBar, subdivision }) {
        const stepsPerBeat = subdivision;
        const isDownbeat = stepIdx % stepsPerBeat === 0;
        const beat = beatNumberInBar(stepIdx, { beatsPerBar, subdivision });
        if (beat === 1 && isDownbeat) return 'bar';
        if (isDownbeat) return 'beat';
        return 'sub';
    }

    function schedulerTick() {
        const audioCtx = getAudioCtx();
        if (!audioCtx) return;
        const now = audioCtx.currentTime;
        const ahead = getScheduleAheadTime();

        while (getNextEventTime() < now + ahead) {
            const tempo = getTempo();
            const stepIdx = getStepIndex();
            const stepsPerBar = tempo.beatsPerBar * tempo.subdivision;
            const stepInBar = ((stepIdx % stepsPerBar) + stepsPerBar) % stepsPerBar;
            const stressLevels = getStressLevels();
            const rawStressLevel = stressLevels[stepInBar] ?? 0;
            const stressLevel = rawStressLevel === 2 ? 2 : rawStressLevel === 1 ? 1 : 0;
            const role = stepRole(stepIdx, tempo);

            if (getDownbeatEnabled() && stepInBar === 0) {
                scheduleDownbeatTone(getNextEventTime());
            }

            scheduleClick(getNextEventTime(), {
                role,
                stressLevel,
                ...getAudioParams(),
            });

            const delayMs = Math.max(0, (getNextEventTime() - audioCtx.currentTime) * 1000);
            if (onVisual) onVisual({ stepIdx, stepInBar, role, delayMs });

            const delta = stepDeltaSecondsForIndex(stepIdx, tempo);
            setNextEventTime(getNextEventTime() + delta);
            setStepIndex(stepIdx + 1);
        }
    }

    return { schedulerTick };
}
